import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Building2, Target, Shield, MapPin, Clock, Award } from 'lucide-react';

// Professional intermodal images
const ABOUT_HERO_IMAGE = 'https://images.unsplash.com/photo-1705239738282-84924db4b056?auto=format&fit=crop&w=1920&q=80';

export default function About() {
  return (
    <div className="min-h-screen bg-[#0A0E1A]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `url('${ABOUT_HERO_IMAGE}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E1A] via-[#0A0E1A]/90 to-[#0A0E1A]" />
        
        <div className="relative z-10 max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tighter uppercase mb-8">
            <span className="text-white">ABOUT </span>
            <span className="text-[#00A6E8]">DLT</span>
          </h1>
          
          <div className="space-y-6 text-lg leading-relaxed text-slate-300">
            <p className="text-xl md:text-2xl">
              DLT is the intermodal logistics division of <span className="text-[#FFA726] font-semibold">Fallou Enterprise INC</span>, based in Columbus, Ohio.
            </p>
            
            <p>
              We specialize in structured container drayage, import coordination, and export logistics solutions 
              for brokers, freight forwarders, direct shippers, and international trade partners.
            </p>
            
            <p>
              Our operational focus is <span className="text-[#00A6E8] font-semibold">reliability</span>, 
              <span className="text-[#00A6E8] font-semibold"> compliance</span>, and 
              <span className="text-[#00A6E8] font-semibold"> disciplined workflow management</span> designed 
              to support both domestic and overseas container movements.
            </p>
          </div>
        </div>
      </section>
      
      {/* Image Banner */}
      <section className="relative h-64 md:h-80 overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1768779533631-40721f91ae56?auto=format&fit=crop&w=1920&q=80"
          alt="Freight train carrying shipping containers"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E1A] via-[#0A0E1A]/40 to-[#0A0E1A]/60" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-3xl md:text-4xl font-bold text-white uppercase tracking-wider">
              Connecting Rail to Road
            </p>
            <p className="text-slate-300 mt-2">Columbus Intermodal Hub</p>
          </div>
        </div>
      </section>
      
      <section className="py-16 px-6" data-testid="about-section">
        <div className="max-w-5xl mx-auto">
          
          {/* Values */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 hover:border-[#00A6E8]/30 transition-colors" data-testid="value-card-reliability">
              <Shield className="text-[#00A6E8] mb-4" size={32} />
              <h3 className="text-xl font-bold mb-2 text-white uppercase">Reliability</h3>
              <p className="text-slate-400">Consistent, dependable service you can trust for every shipment.</p>
            </div>
            
            <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 hover:border-[#FFA726]/30 transition-colors" data-testid="value-card-compliance">
              <Target className="text-[#FFA726] mb-4" size={32} />
              <h3 className="text-xl font-bold mb-2 text-white uppercase">Compliance</h3>
              <p className="text-slate-400">Full regulatory adherence and transparent documentation.</p>
            </div>
            
            <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 hover:border-white/30 transition-colors" data-testid="value-card-efficiency">
              <Building2 className="text-white mb-4" size={32} />
              <h3 className="text-xl font-bold mb-2 text-white uppercase">Efficiency</h3>
              <p className="text-slate-400">Streamlined workflows designed for speed and accuracy.</p>
            </div>
          </div>
          
          {/* Stats Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            <div className="text-center p-6 bg-[#0F172A]/50 rounded-lg">
              <MapPin className="text-[#00A6E8] mx-auto mb-2" size={28} />
              <p className="text-2xl font-bold text-white">Columbus</p>
              <p className="text-slate-500 text-sm">Ohio Hub</p>
            </div>
            <div className="text-center p-6 bg-[#0F172A]/50 rounded-lg">
              <Clock className="text-[#FFA726] mx-auto mb-2" size={28} />
              <p className="text-2xl font-bold text-white">24/7</p>
              <p className="text-slate-500 text-sm">Operations</p>
            </div>
            <div className="text-center p-6 bg-[#0F172A]/50 rounded-lg">
              <Award className="text-[#00A6E8] mx-auto mb-2" size={28} />
              <p className="text-2xl font-bold text-white">CSX & NS</p>
              <p className="text-slate-500 text-sm">Rail Partners</p>
            </div>
            <div className="text-center p-6 bg-[#0F172A]/50 rounded-lg">
              <Building2 className="text-[#FFA726] mx-auto mb-2" size={28} />
              <p className="text-2xl font-bold text-white">Full Service</p>
              <p className="text-slate-500 text-sm">Drayage</p>
            </div>
          </div>
          
          {/* Credentials */}
          <div className="p-8 bg-[#0F172A] border border-white/10 rounded-lg">
            <h3 className="text-2xl font-bold mb-6 text-white uppercase">Regulatory Identifiers</h3>
            <div className="flex flex-wrap gap-8 text-lg">
              <div className="bg-[#1E293B] px-6 py-4 rounded-lg">
                <span className="text-slate-400 block text-sm">US DOT Number</span>
                <span className="text-[#00A6E8] font-bold text-2xl">1662372</span>
              </div>
              <div className="bg-[#1E293B] px-6 py-4 rounded-lg">
                <span className="text-slate-400 block text-sm">MC Number</span>
                <span className="text-[#FFA726] font-bold text-2xl">657445</span>
              </div>
            </div>
            <p className="text-slate-500 text-sm mt-6">
              Fallou Enterprise INC dba DLT is a federally registered motor carrier operating in compliance with all FMCSA regulations.
            </p>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
